import { PlainButton } from "@jugl-web/ui-components";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useToast, useTranslations } from "@jugl-web/utils";
import { ChatbotPreviewBackground } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/screens/WebChatBotAccountScreen/components/ChatbotPreviewBackground";
import { useManageAccountsDrawer } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { useEmailAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/EmailAccountProvider/useEmailAccount";
import React, { ReactNode, useCallback } from "react";
import { Alert, Button } from "@jugl-web/ui-kit";
import { useConfirmationDialogState } from "@jugl-web/utils/hooks/useConfirmationDialogState";
import { ReactComponent as SettingsIcon } from "./assets/settings.svg";
import { ReactComponent as GmailIcon } from "./assets/gmail.svg";
import aiAgentImage from "./assets/ai-agent.png";
import manualHandImage from "./assets/manual-hand.png";

export const ChannelPreviewUnavailable: React.FC<{ entityId: string }> = ({
  entityId,
}) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { integrationsApi } = useRestApiProvider();
  const { emailAccount } = useEmailAccount();
  const { openManageAccountDrawer } = useManageAccountsDrawer();
  const [updateEmailConfig, { isLoading: isSwitchingReplyMode }] =
    integrationsApi.useUpdateEmailConfigMutation();
  const responseTypeDialogState = useConfirmationDialogState<{
    mode: "manual" | "ai";
  }>();

  const isAiReplyEnabled = emailAccount?.ai_reply === true;

  const handleSwitchReplyMode = useCallback(async () => {
    if (isSwitchingReplyMode) {
      return;
    }

    try {
      await updateEmailConfig({
        entityId,
        ai_reply: !isAiReplyEnabled,
      }).unwrap();
      responseTypeDialogState.close();
      toast(
        isAiReplyEnabled
          ? t({
              id: "cx-ai-agents-page.gmail-channel-assigned-to-manual-response",
              defaultMessage: "Gmail channel assigned to manual response",
            })
          : t({
              id: "cx-ai-agents-page.gmail-channel-assigned-to-ai-agent-response",
              defaultMessage: "Gmail channel assigned to AI Agent response",
            })
      );
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    }
  }, [
    entityId,
    isAiReplyEnabled,
    isSwitchingReplyMode,
    responseTypeDialogState,
    t,
    toast,
    updateEmailConfig,
  ]);

  return (
    <>
      <ChatbotPreviewBackground>
        <div className="flex h-full w-full animate-fade-in items-center justify-center p-10">
          <div className="flex w-full max-w-[710px] flex-col gap-10">
            <div className="flex items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <GmailIcon className="drop-shadow-[-10.125px_11.25px_13.275px_rgba(60,60,60,0.16)]" />
                <div className="flex flex-col gap-2">
                  <span className="font-secondary text-[22px] font-medium text-black">
                    {t({
                      id: "cx-ai-agents-page.gmail-channel-is-live",
                      defaultMessage: "Gmail channel is Live",
                    })}
                  </span>
                  <span className="font-secondary text-sm text-[#828282] italic">
                    {t({
                      id: "cx-ai-agents-page.gmail-channel-preview-unavailable",
                      defaultMessage:
                        "* Preview of Gmail channel is not available",
                    })}
                  </span>
                </div>
              </div>

              <Button
                onClick={() =>
                  openManageAccountDrawer({ name: "emailAccount" })
                }
                variant="secondary"
                iconEnd={<SettingsIcon />}
              >
                {t({
                  id: "common.settings",
                  defaultMessage: "Settings",
                })}
              </Button>
            </div>

            <div className="flex items-center justify-between gap-1 rounded-2xl bg-grey-100 p-8">
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-1">
                  <span className="font-secondary leading-4 font-semibold text-dark">
                    {isAiReplyEnabled
                      ? t({
                          id: "cx-ai-agents-page.conversations-handled-by-ai-agent",
                          defaultMessage: "Conversations handled by AI Agent",
                        })
                      : t({
                          id: "cx-ai-agents-page.conversations-handled-manually",
                          defaultMessage: "Conversations handled manually",
                        })}
                  </span>
                  <span className="font-secondary text-sm leading-[21px] text-dark-600">
                    {isAiReplyEnabled
                      ? t(
                          {
                            id: "cx-ai-agents-page.ai-agent-replies-description",
                            defaultMessage:
                              "Agent send replies automatically{br}and transfer to human agent if needed",
                          },
                          { br: <br /> }
                        )
                      : t({
                          id: "cx-ai-agents-page.team-replies-manually",
                          defaultMessage:
                            "Team replies manually without AI Assistance",
                        })}
                  </span>
                </div>
                <PlainButton
                  className="w-max font-secondary text-[13px] leading-[150%] font-medium text-primary-800 underline disabled:opacity-50"
                  onClick={() =>
                    responseTypeDialogState.open({
                      onConfirm: handleSwitchReplyMode,
                      metadata: { mode: isAiReplyEnabled ? "manual" : "ai" },
                    })
                  }
                  disabled={isSwitchingReplyMode}
                >
                  {isAiReplyEnabled
                    ? t({
                        id: "cx-ai-agents-page.switch-to-manual-replies",
                        defaultMessage: "Switch to manual replies",
                      })
                    : t({
                        id: "cx-ai-agents-page.switch-to-ai-agent-replies",
                        defaultMessage: "Switch to AI Agent replies",
                      })}
                </PlainButton>
              </div>

              <img
                src={isAiReplyEnabled ? aiAgentImage : manualHandImage}
                alt=""
                className="h-auto w-[142px] shrink-0 object-contain"
              />
            </div>
          </div>
        </div>
      </ChatbotPreviewBackground>
      <Alert
        isOpen={responseTypeDialogState.isOpen}
        onRequestClose={responseTypeDialogState.close}
        title={t({
          id: "cx-ai-agents-page.response-type",
          defaultMessage: "Response type",
        })}
        content={
          responseTypeDialogState.metadata?.mode === "manual"
            ? t(
                {
                  id: "cx-ai-agents-page.switch-to-manual-response-confirmation",
                  defaultMessage:
                    "Are you sure you want to assign <highlightedText>Manual response</highlightedText> for your Gmail channel? Your Team will reply manually without AI Assistance",
                },
                {
                  highlightedText: (text: ReactNode) => (
                    <span className="bg-linear-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                      {text}
                    </span>
                  ),
                }
              )
            : t(
                {
                  id: "cx-ai-agents-page.assign-ai-agent-response-confirmation",
                  defaultMessage:
                    "Are you sure you want to assign <highlightedText>AI Agent response</highlightedText> for your Gmail channel? Agent will send replies automatically and transfer to human agent if needed",
                },
                {
                  highlightedText: (text: ReactNode) => (
                    <span className="bg-linear-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                      {text}
                    </span>
                  ),
                }
              )
        }
        buttons={[
          {
            text: t({ id: "common.cancel", defaultMessage: "Cancel" }),
            role: "close",
          },
          {
            text: t({ id: "common.confirm", defaultMessage: "Confirm" }),
            onClick: () => responseTypeDialogState.confirm(),
            isDisabled: isSwitchingReplyMode,
          },
        ]}
      />
    </>
  );
};
